import Decimal from "decimal.js";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("lib/utils/token-precision.ts");

/**
 * 符文代币精度计算工具类
 * 根据代币的divisibility和decimals字段动态计算精度系数
 */
export class TokenPrecisionUtils {
	/**
	 * 根据代币信息计算精度系数
	 * @param tokenData 代币数据对象（包含divisibility、decimals字段）
	 * @returns 精度系数（如1e11, 1e5等）
	 */
	static getPrecisionFactor(tokenData: any): number {
		// 只要有精度信息就进行动态计算，不依赖external字段
		if (
			tokenData?.divisibility !== undefined &&
			tokenData.decimals !== undefined
		) {
			const totalPrecision = tokenData.divisibility + tokenData.decimals;
			return 10 ** totalPrecision;
		}

		// 缺少精度信息时，使用默认值（兼容旧版本和内部符文）
		return 1e11;
	}

	/**
	 * 将API余额转换为前端显示数量
	 * @param balance API返回的原始余额字符串
	 * @param tokenData 代币数据对象
	 * @returns 前端显示的数量字符串
	 */
	static balanceToDisplay(balance: string | number, tokenData: any): string {
		try {
			const factor = TokenPrecisionUtils.getPrecisionFactor(tokenData);
			return new Decimal(balance).div(factor).toString();
		} catch (error) {
			uiLog.error("余额转换失败:", error);
			return "0";
		}
	}

	/**
	 * 将前端显示数量转换为API格式
	 * @param amount 前端输入的数量
	 * @param tokenData 代币数据对象
	 * @returns API格式的数量字符串
	 */
	static displayToApi(amount: string | number, tokenData: any): string {
		try {
			const factor = TokenPrecisionUtils.getPrecisionFactor(tokenData);
			return new Decimal(amount).mul(factor).toFixed(0, Decimal.ROUND_DOWN);
		} catch (error) {
			uiLog.error("数量转换失败:", error);
			return "0";
		}
	}

	/**
	 * 将前端显示数量转换为BigInt格式（用于交易）
	 * @param amount 前端输入的数量
	 * @param tokenData 代币数据对象
	 * @returns BigInt格式的数量
	 */
	static displayToApiBigInt(amount: string | number, tokenData: any): bigint {
		try {
			const apiAmount = TokenPrecisionUtils.displayToApi(amount, tokenData);
			return BigInt(apiAmount);
		} catch (error) {
			uiLog.error("数量转换为BigInt失败:", error);
			return BigInt(0);
		}
	}

	/**
	 * 获取代币精度信息摘要（用于调试）
	 * @param tokenData 代币数据对象
	 * @returns 精度信息字符串
	 */
	static getPrecisionSummary(tokenData: any): string {
		const hasPrecisionData =
			tokenData?.divisibility !== undefined &&
			tokenData?.decimals !== undefined;
		const isExternal = tokenData?.external === true;
		const divisibility = tokenData?.divisibility ?? "undefined";
		const decimals = tokenData?.decimals ?? "undefined";
		const factor = TokenPrecisionUtils.getPrecisionFactor(tokenData);

		const typeInfo = hasPrecisionData
			? "动态精度"
			: isExternal
				? "外部符文(默认)"
				: "内部符文(默认)";
		return `${typeInfo}, divisibility=${divisibility}, decimals=${decimals}, factor=${factor}`;
	}

	/**
	 * 检查代币是否为外部符文
	 * @param tokenData 代币数据对象
	 * @returns 是否为外部符文
	 */
	static isExternalRune(tokenData: any): boolean {
		return tokenData?.external === true;
	}

	/**
	 * 检查是否使用了动态精度计算
	 * @param tokenData 代币数据对象
	 * @returns 是否使用了动态精度
	 */
	static isUsingDynamicPrecision(tokenData: any): boolean {
		return (
			tokenData?.divisibility !== undefined && tokenData?.decimals !== undefined
		);
	}

	/**
	 * 格式化余额显示（保留适当小数位）
	 * @param balance API返回的原始余额
	 * @param tokenData 代币数据对象
	 * @param maxDecimals 最大小数位数，默认为6
	 * @returns 格式化后的显示数量
	 */
	static formatBalanceDisplay(
		balance: string | number,
		tokenData: any,
		maxDecimals: number = 6,
	): string {
		try {
			const displayAmount = TokenPrecisionUtils.balanceToDisplay(
				balance,
				tokenData,
			);
			const decimal = new Decimal(displayAmount);

			// 如果是整数，直接返回
			if (decimal.isInteger()) {
				return decimal.toFixed(0);
			}

			// 保留指定小数位，去除尾部零
			return decimal
				.toFixed(maxDecimals, Decimal.ROUND_DOWN)
				.replace(/\.?0+$/, "");
		} catch (error) {
			uiLog.error("格式化余额显示失败:", error);
			return "0";
		}
	}

	/**
	 * 将API价格转换为Sats显示单位
	 * API价格如514315，显示为514.315 Sats
	 * @param apiPrice API返回的价格
	 * @returns Sats单位的价格字符串
	 */
	static priceToSats(apiPrice: string | number): string {
		try {
			// API价格 ÷ 1000 = Sats价格
			return new Decimal(apiPrice).div(1000).toString();
		} catch (error) {
			uiLog.error("价格转换为Sats失败:", error);
			return "0";
		}
	}

	/**
	 * 将API价格转换为BTC单位（用于价值计算）
	 * API价格如23670表示23.67 sats，需要转换为BTC
	 * @param apiPrice API返回的价格（sats × 1000格式）
	 * @returns BTC单位的价格（用于与符文数量相乘）
	 */
	static priceToBtc(apiPrice: string | number): string {
		try {
			// API价格先÷1000得到sats，再÷1e8得到BTC
			// 等价于 API价格 ÷ 1e11 = BTC价格
			return new Decimal(apiPrice).div(1e11).toString();
		} catch (error) {
			uiLog.error("价格转换为BTC失败:", error);
			return "0";
		}
	}

	/**
	 * 格式化价格显示（Sats单位，保留适当小数位）
	 * @param apiPrice API返回的价格
	 * @param maxDecimals 最大小数位数，默认为2
	 * @returns 格式化后的Sats价格显示
	 */
	static formatPriceDisplay(
		apiPrice: string | number,
		maxDecimals: number = 2,
	): string {
		try {
			const satsPrice = TokenPrecisionUtils.priceToSats(apiPrice);
			const decimal = new Decimal(satsPrice);

			// 如果是整数，直接返回
			if (decimal.isInteger()) {
				return decimal.toFixed(0);
			}

			// 保留指定小数位，去除尾部零
			return decimal.toFixed(maxDecimals).replace(/\.?0+$/, "");
		} catch (error) {
			uiLog.error("格式化价格显示失败:", error);
			return "0";
		}
	}

	/**
	 * 将API价格转换为美元显示值
	 * @param apiPrice API返回的价格（sats × 1000格式）
	 * @param btcUsdPrice 当前BTC兑美元价格
	 * @returns 美元显示字符串；无法计算时返回null
	 */
	static formatUsdPriceDisplay(
		apiPrice: string | number,
		btcUsdPrice: number,
	): string | null {
		if (!btcUsdPrice || btcUsdPrice <= 0) return null;

		try {
			const usdValue = new Decimal(TokenPrecisionUtils.priceToBtc(apiPrice)).mul(
				btcUsdPrice,
			);

			if (!usdValue.isFinite() || usdValue.lte(0)) return null;
			if (usdValue.lt(0.000001)) return "< $0.000001";
			if (usdValue.gte(1)) return `$${usdValue.toFixed(2)}`;
			if (usdValue.gte(0.01)) return `$${usdValue.toFixed(4)}`;
			return `$${usdValue.toFixed(6)}`;
		} catch (error) {
			uiLog.error("格式化美元价格显示失败:", error);
			return null;
		}
	}

	/**
	 * 计算符文数量对应的BTC价值
	 * @param runeAmount 符文数量（前端显示数量）
	 * @param apiPrice API价格（1e11精度）
	 * @param tokenData 代币数据对象
	 * @returns BTC价值字符串
	 */
	static calculateBtcValue(
		runeAmount: string | number,
		apiPrice: string | number,
		tokenData: any,
	): string {
		try {
			const btcPrice = TokenPrecisionUtils.priceToBtc(apiPrice);
			const runeAmountDecimal = new Decimal(runeAmount);
			return runeAmountDecimal.mul(btcPrice).toString();
		} catch (error) {
			uiLog.error("计算BTC价值失败:", error);
			return "0";
		}
	}
}
